import type { LatLng, LatLngBounds, LatLngTuple } from 'leaflet'
import { lineString, point, polygon } from '@turf/turf'

export type LngLat = [number, number]
export type LngLatLine = LngLat[]
export type LngLatPolygon = LngLatLine[]
export type LngLatBounds = [LngLat, LngLat]

/**
 * Конвертирует координату бизнес-логики в формат Leaflet.
 *
 * @param coordinates — координата в формате [lng, lat]
 * @returns координата в формате [lat, lng]
 */
export const toLeaflet = ([lng, lat]: LngLat): LatLngTuple => [lat, lng]

/**
 * Конвертирует tuple-координату Leaflet в формат бизнес-логики.
 *
 * @param coordinates — координата в формате [lat, lng]
 * @returns координата в формате [lng, lat]
 */
export const fromLeafletTuple = ([lat, lng]: LatLngTuple): LngLat => [lng, lat]

/**
 * Конвертирует объект координаты Leaflet в формат бизнес-логики.
 *
 * @param coordinates — объект L.LatLng
 * @returns координата в формате [lng, lat]
 */
export const fromLeaflet = (coordinates: LatLng): LngLat => [coordinates.lng, coordinates.lat]

/**
 * Конвертирует линию бизнес-логики в формат Leaflet.
 *
 * @param coordinates — координаты линии в формате [lng, lat][]
 * @returns координаты линии в формате [lat, lng][]
 */
export const lineToLeaflet = (coordinates: LngLatLine): LatLngTuple[] => coordinates.map(toLeaflet)

/**
 * Конвертирует tuple-линию Leaflet в формат бизнес-логики.
 *
 * @param coordinates — координаты линии в формате [lat, lng][]
 * @returns координаты линии в формате [lng, lat][]
 */
export const lineFromLeaflet = (coordinates: LatLngTuple[]): LngLatLine =>
  coordinates.map(fromLeafletTuple)

/**
 * Конвертирует полигон бизнес-логики в формат Leaflet.
 *
 * @param coordinates — координаты полигона в формате [lng, lat][][]
 * @returns координаты полигона в формате [lat, lng][][]
 */
export const polygonToLeaflet = (coordinates: LngLatPolygon): LatLngTuple[][] =>
  coordinates.map(lineToLeaflet)

/**
 * Конвертирует tuple-полигон Leaflet в формат бизнес-логики.
 *
 * @param coordinates — координаты полигона в формате [lat, lng][][]
 * @returns координаты полигона в формате [lng, lat][][]
 */
export const polygonFromLeaflet = (coordinates: LatLngTuple[][]): LngLatPolygon =>
  coordinates.map(lineFromLeaflet)

/**
 * Конвертирует границы Leaflet в формат бизнес-логики.
 *
 * @param bounds — объект L.LatLngBounds
 * @returns границы в формате [[minLng, minLat], [maxLng, maxLat]]
 */
export const boundsFromLeaflet = (bounds: LatLngBounds): LngLatBounds => [
  [bounds.getWest(), bounds.getSouth()],
  [bounds.getEast(), bounds.getNorth()],
]

/**
 * Строит GeoJSON geometry-строку из координат бизнес-логики через turf.js.
 *
 * @param coordinates — координаты в формате [lng, lat][]
 * @returns JSON-строка GeoJSON geometry
 */
export const buildGeoJsonGeometryString = (coordinates: LngLatLine | undefined): string => {
  if (!coordinates?.length) return ''

  if (coordinates.length === 1) {
    return JSON.stringify(point(coordinates[0]!).geometry)
  }

  const first = coordinates[0]!
  const last = coordinates[coordinates.length - 1]!

  if (first[0] === last[0] && first[1] === last[1]) {
    return JSON.stringify(polygon([coordinates]).geometry)
  }

  return JSON.stringify(lineString(coordinates).geometry)
}
